import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { getTileCollection } from "@src/cloud-connect/dependency-injection-container/get-tile-collection";
import { Tile } from "@src/cloud-connect/blueprint/dashboards/entities/tile";
import { TimePeriod } from "@src/cloud-connect/blueprint/dashboards/entities/time-period";
import { DrawerService } from "@src/cloud-connect/drawer-service/drawer-service";
import { DrawerIds } from "@src/cloud-connect/drawer-service/drawer-ids";
import { useI18N } from "@src/language/language-context";

export interface TileDrawerModalObj {
  buildEmptyTile: () => {
    id: string;
    title: string;
    position: string;
    grid_area: string;
    time_period_amount: string;
    time_period_time_unit: string;
    use_aggregated_for_single_node: boolean;
  };
  cancelCreateNewTileForm: () => void;
  cancelEditTileForm: () => void;
  closeTileDrawerModal: () => void;
  createNewTile: (tile: any) => void;
  deleteTile: (id: string) => void;
  drawerContent: string;
  loadTileToEdit: (id: string) => void;
  localTiles: any[];
  saveTile: (tile: any) => void;
  showCreateNewTileForm: () => void;
  texts: {
    title: string;
    create: string;
    close: string;
    createNewTile: string;
    editTile: string;
  };
  tileToEdit: any;
}

export const useTileDrawerModalHook = (): TileDrawerModalObj => {
  const i18n = useI18N();
  const texts = {
    title: i18n("modules.tileDrawerModal.title"),
    create: i18n("modules.tileDrawerModal.create"),
    close: i18n("modules.tileDrawerModal.close"),
    createNewTile: i18n("modules.tileDrawerModal.createNewTile"),
    editTile: i18n("modules.tileDrawerModal.editTile"),
  };

  const [drawerContent, setDrawerContent] = useState("showTilesList");
  const [tileToEdit, setTileToEdit] = useState(undefined);
  const [localTiles, setLocalTiles] = useState(undefined);

  const buildEmptyTile = () => {
    const uuid = uuidv4();
    return {
      id: uuid,
      title: "",
      position: "",
      grid_area: "quarter",
      frequency: "quarterhourly",
      time_period_amount: "",
      time_period_time_unit: "seconds",
      use_aggregated_for_single_node: false,
    };
  };

  const cancelCreateNewTileForm = () => {
    console.log("cancelCreateNewTileForm");
    setDrawerContent("showTilesList");
  };

  const cancelEditTileForm = () => {
    console.log("cancelEditTileForm");
    setDrawerContent("showTilesList");
  };

  const closeTileDrawerModal = () => {
    new DrawerService().closeTileDrawerModal(DrawerIds.TileDrawer);
  };

  const createNewTile = (tile) => {
    console.log("createNewTile", tile);
    setLocalTiles([...localTiles, tile]);
    getTileCollection().addTile(
      new Tile(
        tile.id,
        tile.title,
        tile.position,
        tile.grid_area,
        tile.frequency,
        new TimePeriod(tile.time_period_amount, tile.time_period_time_unit),
        [],
        tile.use_aggregated_for_single_node,
      ),
    );
    setDrawerContent("showTilesList");
  };

  const deleteTile = (id) => {
    console.log("deleteTile", id);
    setLocalTiles(localTiles.filter((tile) => tile.id !== id));
    getTileCollection().removeTileById(id);
  };

  const loadTileToEdit = (id) => {
    console.log("loadTileToEdit here", id);
    const tile = localTiles.find((tile) => tile.id === id);

    if (tile.time_period !== undefined) {
      setTileToEdit({
        id: tile.id,
        title: tile.title,
        position: tile.position,
        grid_area: tile.grid_area,
        frequency: tile.frequency,
        time_period_amount: tile.time_period.amount,
        time_period_time_unit: tile.time_period.time_unit,
        nodes: tile.nodes,
        use_aggregated_for_single_node:
          tile.use_aggregated_for_single_node || false,
      });
    } else {
      setTileToEdit(tile);
    }

    setDrawerContent("editTile");
  };

  const saveTile = (tile) => {
    console.log("saveTile");
    setLocalTiles((localTiles) =>
      localTiles.map((item) =>
        item.id === tile.id
          ? {
              ...item,
              title: tile.title,
              position: tile.position,
              grid_area: tile.grid_area,
              frequency: tile.frequency,
              time_period_amount: tile.time_period_amount,
              time_period_time_unit: tile.time_period_time_unit,
              use_aggregated_for_single_node:
                tile.use_aggregated_for_single_node,
            }
          : item,
      ),
    );

    const newTile = new Tile(
      tile.id,
      tile.title,
      tile.position,
      tile.grid_area,
      tile.frequency,
      new TimePeriod(tile.time_period_amount, tile.time_period_time_unit),
      tile.nodes,
      tile.use_aggregated_for_single_node,
    );

    const tiles = getTileCollection().getTiles();
    const newTiles = [];
    tiles.forEach((tile) => {
      if (tile.getId() === newTile.getId()) {
        newTiles.push(newTile);
      } else {
        newTiles.push(tile);
      }
    });

    getTileCollection().setTiles(newTiles);

    setDrawerContent("showTilesList");
  };

  const showCreateNewTileForm = () => {
    console.log("showCreateNewTileForm");
    setDrawerContent("createNewTile");
  };

  useEffect(() => {
    const tiles = getTileCollection().getTiles();
    if (tiles) {
      setLocalTiles(tiles);
    }
  }, []);

  return {
    buildEmptyTile: buildEmptyTile,
    cancelCreateNewTileForm: cancelCreateNewTileForm,
    cancelEditTileForm: cancelEditTileForm,
    closeTileDrawerModal: closeTileDrawerModal,
    createNewTile: createNewTile,
    deleteTile: deleteTile,
    drawerContent: drawerContent,
    loadTileToEdit: loadTileToEdit,
    localTiles: localTiles,
    saveTile: saveTile,
    showCreateNewTileForm: showCreateNewTileForm,
    texts: texts,
    tileToEdit: tileToEdit,
  };
};
